import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { landingSettingsAPI, uploadAPI, publicAPI } from '@/lib/api';
import { 
  Save, Eye, Upload, Image as ImageIcon, Trash2, Plus, Edit2, 
  Settings, Layout, BarChart3, Video, BookOpen, MessageSquare,
  Phone, Mail, MapPin, ExternalLink, RefreshCw, CheckCircle
} from 'lucide-react';

export default function LandingSettingsTab() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const [imageGalleryOpen, setImageGalleryOpen] = useState(false);
  const [selectedImageField, setSelectedImageField] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);

  // Predefined image gallery
  const imageGallery = [
    { url: 'https://images.pexels.com/photos/5905709/pexels-photo-5905709.jpeg?auto=compress&cs=tinysrgb&w=600', label: 'Siswa Belajar Online 1' },
    { url: 'https://images.pexels.com/photos/4145153/pexels-photo-4145153.jpeg?auto=compress&cs=tinysrgb&w=400', label: 'Siswa Belajar 2' },
    { url: 'https://images.pexels.com/photos/4145190/pexels-photo-4145190.jpeg?auto=compress&cs=tinysrgb&w=400', label: 'Pembelajaran Online' },
    { url: 'https://images.pexels.com/photos/5212320/pexels-photo-5212320.jpeg?auto=compress&cs=tinysrgb&w=400', label: 'Siswa dengan Laptop' },
    { url: 'https://images.pexels.com/photos/267885/pexels-photo-267885.jpeg?auto=compress&cs=tinysrgb&w=400', label: 'Perpustakaan' },
    { url: 'https://images.pexels.com/photos/8112181/pexels-photo-8112181.jpeg?auto=compress&cs=tinysrgb&w=400', label: 'Kampus' },
    { url: 'https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=400', label: 'Tim Kerja' },
    { url: 'https://images.pexels.com/photos/5212345/pexels-photo-5212345.jpeg?auto=compress&cs=tinysrgb&w=400', label: 'Kelas' },
    { url: 'https://images.pexels.com/photos/4050315/pexels-photo-4050315.jpeg?auto=compress&cs=tinysrgb&w=400', label: 'Siswa Asia 1' },
    { url: 'https://images.pexels.com/photos/5553060/pexels-photo-5553060.jpeg?auto=compress&cs=tinysrgb&w=400', label: 'Siswa Asia 2' },
    { url: 'https://images.pexels.com/photos/6147276/pexels-photo-6147276.jpeg?auto=compress&cs=tinysrgb&w=400', label: 'Siswa Menulis' },
    { url: 'https://images.pexels.com/photos/4145354/pexels-photo-4145354.jpeg?auto=compress&cs=tinysrgb&w=400', label: 'Webinar' },
  ];

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const res = await landingSettingsAPI.get();
      setSettings(res.data);
    } catch (error) {
      console.error('Failed to load settings:', error);
      toast.error('Gagal memuat pengaturan');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await landingSettingsAPI.update(settings);
      toast.success('Pengaturan berhasil disimpan');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Gagal menyimpan pengaturan');
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const updateArrayItem = (arrayKey, index, field, value) => {
    setSettings(prev => {
      const arr = [...(prev[arrayKey] || [])];
      arr[index] = { ...arr[index], [field]: value };
      return { ...prev, [arrayKey]: arr };
    });
  };

  const addArrayItem = (arrayKey, defaultItem) => {
    setSettings(prev => ({
      ...prev,
      [arrayKey]: [...(prev[arrayKey] || []), defaultItem]
    }));
  };

  const removeArrayItem = (arrayKey, index) => {
    setSettings(prev => ({
      ...prev,
      [arrayKey]: prev[arrayKey].filter((_, i) => i !== index)
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    if (!file.type.startsWith('image/')) {
      toast.error('File harus berupa gambar');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Ukuran file maksimal 5MB');
      return;
    }

    try {
      setUploadingImage(true);
      const res = await uploadAPI.uploadImage(file);
      if (selectedImageField) {
        if (selectedImageField.arrayKey) {
          updateArrayItem(selectedImageField.arrayKey, selectedImageField.index, 'image', res.data.url);
        } else {
          updateSetting(selectedImageField.key, res.data.url);
        }
        toast.success('Gambar berhasil diupload');
        setImageGalleryOpen(false);
      }
    } catch (error) {
      console.error('Failed to upload:', error);
      toast.error('Gagal mengupload gambar');
    } finally {
      setUploadingImage(false);
    }
  };

  const selectFromGallery = (url) => {
    if (selectedImageField) {
      if (selectedImageField.arrayKey) {
        updateArrayItem(selectedImageField.arrayKey, selectedImageField.index, 'image', url);
      } else {
        updateSetting(selectedImageField.key, url);
      }
      toast.success('Gambar berhasil dipilih');
      setImageGalleryOpen(false);
    }
  };

  const openImageSelector = (field) => {
    setSelectedImageField(field);
    setImageGalleryOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-[#4472C4]" />
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Gagal memuat pengaturan</p>
        <Button onClick={loadSettings} className="mt-4">Coba Lagi</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Pengaturan Landing Page</h1>
          <p className="text-slate-500 mt-1">Kustomisasi konten dan tampilan halaman utama</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setPreviewOpen(true)} data-testid="preview-btn">
            <Eye className="w-4 h-4 mr-2" />
            Preview
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#4472C4] hover:bg-[#3861a8]" data-testid="save-settings-btn">
            {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Simpan
          </Button>
        </div>
      </div>

      {/* Section Tabs */}
      <Tabs value={activeSection} onValueChange={setActiveSection}>
        <TabsList className="grid grid-cols-4 lg:grid-cols-8 gap-1 h-auto p-1 bg-slate-100">
          <TabsTrigger value="hero" className="text-xs py-2">
            <Layout className="w-3 h-3 mr-1" /> Hero
          </TabsTrigger>
          <TabsTrigger value="stats" className="text-xs py-2">
            <BarChart3 className="w-3 h-3 mr-1" /> Stats
          </TabsTrigger>
          <TabsTrigger value="commitment" className="text-xs py-2">
            <CheckCircle className="w-3 h-3 mr-1" /> Komitmen
          </TabsTrigger>
          <TabsTrigger value="tryouts" className="text-xs py-2">
            <BookOpen className="w-3 h-3 mr-1" /> Try Out
          </TabsTrigger>
          <TabsTrigger value="videos" className="text-xs py-2">
            <Video className="w-3 h-3 mr-1" /> Video
          </TabsTrigger>
          <TabsTrigger value="programs" className="text-xs py-2">
            <Settings className="w-3 h-3 mr-1" /> Program
          </TabsTrigger>
          <TabsTrigger value="ebooks" className="text-xs py-2">
            <BookOpen className="w-3 h-3 mr-1" /> E-Book
          </TabsTrigger>
          <TabsTrigger value="contact" className="text-xs py-2">
            <Phone className="w-3 h-3 mr-1" /> Kontak
          </TabsTrigger>
          <TabsTrigger value="bank" className="text-xs py-2">
            <Settings className="w-3 h-3 mr-1" /> Rekening
          </TabsTrigger>
        </TabsList>

        {/* Hero Section */}
        <TabsContent value="hero" className="space-y-4">
          <Card className="p-6">
            <h3 className="font-semibold text-lg mb-4">Hero Section</h3>
            <div className="grid gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Badge Text</label>
                <Input 
                  value={settings.hero_badge_text || ''} 
                  onChange={(e) => updateSetting('hero_badge_text', e.target.value)}
                  placeholder="Platform Try Out Online Terpercaya"
                  data-testid="hero-badge-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Subtitle</label>
                <Input 
                  value={settings.hero_subtitle || ''} 
                  onChange={(e) => updateSetting('hero_subtitle', e.target.value)}
                  placeholder="Selamat Datang di"
                  data-testid="hero-subtitle-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Judul Utama</label>
                <Input 
                  value={settings.hero_title || ''} 
                  onChange={(e) => updateSetting('hero_title', e.target.value)}
                  placeholder="Bimbel My Tentor"
                  data-testid="hero-title-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Deskripsi</label>
                <textarea 
                  className="w-full border rounded-md p-2 text-sm"
                  rows={3}
                  value={settings.hero_description || ''} 
                  onChange={(e) => updateSetting('hero_description', e.target.value)}
                  placeholder="Platform Try Out & Belajar Online..."
                  data-testid="hero-description-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">Hero Image</label>
                <div className="flex gap-4 items-start">
                  {settings.hero_image && (
                    <img src={settings.hero_image} alt="Hero" className="w-32 h-24 object-cover rounded-lg border" />
                  )}
                  <Button 
                    variant="outline" 
                    onClick={() => openImageSelector({ key: 'hero_image' })}
                    data-testid="hero-image-btn"
                  >
                    <ImageIcon className="w-4 h-4 mr-2" />
                    Ganti Gambar
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* Stats Section */}
        <TabsContent value="stats" className="space-y-4">
          <Card className="p-6">
            <h3 className="font-semibold text-lg mb-4">Statistik Landing Page</h3>
            <p className="text-sm text-slate-500 mb-4">Atur label dan nilai untuk setiap statistik yang ditampilkan di landing page.</p>
            
            <div className="space-y-6">
              {/* Tingkat Kelulusan */}
              <div className="grid md:grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <label className="text-sm font-medium text-slate-700">Label Tingkat Kelulusan</label>
                  <Input 
                    value={settings.stats_pass_rate_label || ''} 
                    onChange={(e) => updateSetting('stats_pass_rate_label', e.target.value)}
                    placeholder="Tingkat Kelulusan"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Nilai Tingkat Kelulusan</label>
                  <Input 
                    value={settings.stats_pass_rate_override || ''} 
                    onChange={(e) => updateSetting('stats_pass_rate_override', e.target.value)}
                    placeholder="Contoh: 95%"
                    className="bg-white font-bold"
                  />
                </div>
              </div>

              {/* Siswa Aktif */}
              <div className="grid md:grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <label className="text-sm font-medium text-slate-700">Label Siswa Aktif</label>
                  <Input 
                    value={settings.stats_users_label || ''} 
                    onChange={(e) => updateSetting('stats_users_label', e.target.value)}
                    placeholder="Siswa Aktif"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Nilai Jumlah Siswa</label>
                  <Input 
                    value={settings.stats_users_override || ''} 
                    onChange={(e) => updateSetting('stats_users_override', e.target.value)}
                    placeholder="Contoh: 3.374"
                    className="bg-white font-bold"
                  />
                </div>
              </div>

              {/* Soal Latihan */}
              <div className="grid md:grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <label className="text-sm font-medium text-slate-700">Label Soal Latihan</label>
                  <Input 
                    value={settings.stats_questions_label || ''} 
                    onChange={(e) => updateSetting('stats_questions_label', e.target.value)}
                    placeholder="Soal Latihan"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Nilai Jumlah Soal</label>
                  <Input 
                    value={settings.stats_questions_override || ''} 
                    onChange={(e) => updateSetting('stats_questions_override', e.target.value)}
                    placeholder="Contoh: 16.868+"
                    className="bg-white font-bold"
                  />
                </div>
              </div>

              {/* Tahun Pengalaman */}
              <div className="grid md:grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <label className="text-sm font-medium text-slate-700">Label Tahun Pengalaman</label>
                  <Input 
                    value={settings.stats_experience_label || ''} 
                    onChange={(e) => updateSetting('stats_experience_label', e.target.value)}
                    placeholder="Tahun Pengalaman"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Nilai Tahun Pengalaman</label>
                  <Input 
                    value={settings.stats_experience_value || ''} 
                    onChange={(e) => updateSetting('stats_experience_value', e.target.value)}
                    placeholder="15+"
                    className="bg-white font-bold"
                  />
                </div>
              </div>
            </div>

            <p className="text-xs text-slate-500 mt-4 p-3 bg-blue-50 rounded-lg">
              <strong>Tips:</strong> Jika nilai dikosongkan, sistem akan menampilkan data real dari database.
            </p>
          </Card>
        </TabsContent>

        {/* Commitment Section */}
        <TabsContent value="commitment" className="space-y-4">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg">Komitmen Kami</h3>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => addArrayItem('commitments', { icon: 'Users', title: '', desc: '' })}
              >
                <Plus className="w-4 h-4 mr-1" /> Tambah
              </Button>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Judul Section</label>
              <Input 
                value={settings.commitment_title || ''} 
                onChange={(e) => updateSetting('commitment_title', e.target.value)}
                placeholder="Komitmen kami untuk kesuksesan Anda"
                className="mb-4"
              />
            </div>
            <div className="space-y-4">
              {(settings.commitments || []).map((item, index) => (
                <div key={index} className="p-4 border rounded-lg bg-slate-50">
                  <div className="flex justify-between items-start mb-3">
                    <Badge variant="secondary">Item {index + 1}</Badge>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-red-500 hover:text-red-600"
                      onClick={() => removeArrayItem('commitments', index)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="grid md:grid-cols-3 gap-3">
                    <div>
                      <label className="text-xs text-slate-500">Icon</label>
                      <Select 
                        value={item.icon} 
                        onValueChange={(v) => updateArrayItem('commitments', index, 'icon', v)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Pilih icon" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Users">Users</SelectItem>
                          <SelectItem value="DollarSign">DollarSign</SelectItem>
                          <SelectItem value="FileText">FileText</SelectItem>
                          <SelectItem value="Award">Award</SelectItem>
                          <SelectItem value="Star">Star</SelectItem>
                          <SelectItem value="CheckCircle">CheckCircle</SelectItem>
                          <SelectItem value="Clock">Clock</SelectItem>
                          <SelectItem value="Trophy">Trophy</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs text-slate-500">Judul</label>
                      <Input 
                        value={item.title || ''} 
                        onChange={(e) => updateArrayItem('commitments', index, 'title', e.target.value)}
                        placeholder="Tentor Terbaik"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-500">Deskripsi</label>
                      <Input 
                        value={item.desc || ''} 
                        onChange={(e) => updateArrayItem('commitments', index, 'desc', e.target.value)}
                        placeholder="Didukung tentor berkualitas..."
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        {/* Try Outs Section */}
        <TabsContent value="tryouts" className="space-y-4">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg">Try Out Unggulan</h3>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => addArrayItem('featured_tryouts', { title: '', questions: 50, duration: 60, price: 0 })}
              >
                <Plus className="w-4 h-4 mr-1" /> Tambah
              </Button>
            </div>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Judul Section</label>
                <Input 
                  value={settings.tryouts_title || ''} 
                  onChange={(e) => updateSetting('tryouts_title', e.target.value)}
                  placeholder="Latihan soal berkualitas"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Subtitle</label>
                <Input 
                  value={settings.tryouts_subtitle || ''} 
                  onChange={(e) => updateSetting('tryouts_subtitle', e.target.value)}
                  placeholder="Ribuan soal dengan pembahasan lengkap..."
                />
              </div>
            </div>
            <div className="space-y-3">
              {(settings.featured_tryouts || []).map((item, index) => (
                <div key={index} className="flex items-center gap-3 p-3 border rounded-lg bg-slate-50">
                  <div className="flex-1 grid grid-cols-4 gap-2">
                    <Input 
                      value={item.title || ''} 
                      onChange={(e) => updateArrayItem('featured_tryouts', index, 'title', e.target.value)}
                      placeholder="Judul Try Out"
                    />
                    <Input 
                      type="number"
                      value={item.questions || 0} 
                      onChange={(e) => updateArrayItem('featured_tryouts', index, 'questions', parseInt(e.target.value) || 0)}
                      placeholder="Jumlah Soal"
                    />
                    <Input 
                      type="number"
                      value={item.duration || 0} 
                      onChange={(e) => updateArrayItem('featured_tryouts', index, 'duration', parseInt(e.target.value) || 0)}
                      placeholder="Durasi (menit)"
                    />
                    <Input 
                      type="number"
                      value={item.price || 0} 
                      onChange={(e) => updateArrayItem('featured_tryouts', index, 'price', parseInt(e.target.value) || 0)}
                      placeholder="Harga (0 = Gratis)"
                    />
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-red-500"
                    onClick={() => removeArrayItem('featured_tryouts', index)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        {/* Videos Section */}
        <TabsContent value="videos" className="space-y-4">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg">Video Pembelajaran</h3>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => addArrayItem('featured_videos', { title: '', views: '0', duration: '0:00', image: '' })}
              >
                <Plus className="w-4 h-4 mr-1" /> Tambah
              </Button>
            </div>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Judul Section</label>
                <Input 
                  value={settings.videos_title || ''} 
                  onChange={(e) => updateSetting('videos_title', e.target.value)}
                  placeholder="Belajar lewat video"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Subtitle</label>
                <Input 
                  value={settings.videos_subtitle || ''} 
                  onChange={(e) => updateSetting('videos_subtitle', e.target.value)}
                  placeholder="Materi pembelajaran dalam format video..."
                />
              </div>
            </div>
            <div className="space-y-3">
              {(settings.featured_videos || []).map((item, index) => (
                <div key={index} className="p-3 border rounded-lg bg-slate-50">
                  <div className="flex items-start gap-3">
                    {item.image && (
                      <img src={item.image} alt="" className="w-24 h-16 object-cover rounded" />
                    )}
                    <div className="flex-1 grid grid-cols-3 gap-2">
                      <Input 
                        value={item.title || ''} 
                        onChange={(e) => updateArrayItem('featured_videos', index, 'title', e.target.value)}
                        placeholder="Judul Video"
                      />
                      <Input 
                        value={item.views || ''} 
                        onChange={(e) => updateArrayItem('featured_videos', index, 'views', e.target.value)}
                        placeholder="Views (e.g. 12,000)"
                      />
                      <Input 
                        value={item.duration || ''} 
                        onChange={(e) => updateArrayItem('featured_videos', index, 'duration', e.target.value)}
                        placeholder="Durasi (e.g. 15:30)"
                      />
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => openImageSelector({ arrayKey: 'featured_videos', index })}
                    >
                      <ImageIcon className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-red-500"
                      onClick={() => removeArrayItem('featured_videos', index)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        {/* Programs Section */}
        <TabsContent value="programs" className="space-y-4">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg">Program Persiapan</h3>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => addArrayItem('featured_programs', { title: '', desc: '', category: '', image: '' })}
              >
                <Plus className="w-4 h-4 mr-1" /> Tambah
              </Button>
            </div>
            <div className="mb-4">
              <label className="text-sm font-medium text-slate-700">Judul Section</label>
              <Input 
                value={settings.programs_title || ''} 
                onChange={(e) => updateSetting('programs_title', e.target.value)}
                placeholder="Pilih program yang sesuai"
              />
            </div>
            <div className="space-y-3">
              {(settings.featured_programs || []).map((item, index) => (
                <div key={index} className="p-3 border rounded-lg bg-slate-50">
                  <div className="flex items-start gap-3">
                    {item.image && (
                      <img src={item.image} alt="" className="w-24 h-16 object-cover rounded" />
                    )}
                    <div className="flex-1 grid grid-cols-3 gap-2">
                      <Input 
                        value={item.title || ''} 
                        onChange={(e) => updateArrayItem('featured_programs', index, 'title', e.target.value)}
                        placeholder="Nama Program"
                      />
                      <Input 
                        value={item.desc || ''} 
                        onChange={(e) => updateArrayItem('featured_programs', index, 'desc', e.target.value)}
                        placeholder="Deskripsi Singkat"
                      />
                      <Input 
                        value={item.category || ''} 
                        onChange={(e) => updateArrayItem('featured_programs', index, 'category', e.target.value)}
                        placeholder="Kategori (e.g. Kelas Akademik)"
                      />
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => openImageSelector({ arrayKey: 'featured_programs', index })}
                    >
                      <ImageIcon className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-red-500"
                      onClick={() => removeArrayItem('featured_programs', index)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        {/* E-Books Section */}
        <TabsContent value="ebooks" className="space-y-4">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg">E-Book Premium</h3>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => addArrayItem('featured_ebooks', { title: '', pages: 0, price: 0 })}
              >
                <Plus className="w-4 h-4 mr-1" /> Tambah
              </Button>
            </div>
            <div className="mb-4">
              <label className="text-sm font-medium text-slate-700">Judul Section</label>
              <Input 
                value={settings.ebooks_title || ''} 
                onChange={(e) => updateSetting('ebooks_title', e.target.value)}
                placeholder="Koleksi ebook berkualitas"
              />
            </div>
            <div className="space-y-3">
              {(settings.featured_ebooks || []).map((item, index) => (
                <div key={index} className="flex items-center gap-3 p-3 border rounded-lg bg-slate-50">
                  <div className="flex-1 grid grid-cols-3 gap-2">
                    <Input 
                      value={item.title || ''} 
                      onChange={(e) => updateArrayItem('featured_ebooks', index, 'title', e.target.value)}
                      placeholder="Judul E-Book"
                    />
                    <Input 
                      type="number"
                      value={item.pages || 0} 
                      onChange={(e) => updateArrayItem('featured_ebooks', index, 'pages', parseInt(e.target.value) || 0)}
                      placeholder="Jumlah Halaman"
                    />
                    <Input 
                      type="number"
                      value={item.price || 0} 
                      onChange={(e) => updateArrayItem('featured_ebooks', index, 'price', parseInt(e.target.value) || 0)}
                      placeholder="Harga (0 = Gratis)"
                    />
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-red-500"
                    onClick={() => removeArrayItem('featured_ebooks', index)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        {/* Contact Section */}
        <TabsContent value="contact" className="space-y-4">
          <Card className="p-6">
            <h3 className="font-semibold text-lg mb-4">CTA & Kontak</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="text-sm font-medium text-slate-700">Judul CTA</label>
                <Input 
                  value={settings.cta_title || ''} 
                  onChange={(e) => updateSetting('cta_title', e.target.value)}
                  placeholder="Siap Raih Kesuksesan?"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Deskripsi CTA</label>
                <Input 
                  value={settings.cta_description || ''} 
                  onChange={(e) => updateSetting('cta_description', e.target.value)}
                  placeholder="Bergabung dengan ribuan siswa..."
                />
              </div>
            </div>
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">Informasi Kontak</h4>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700">Nomor Telepon</label>
                  <Input 
                    value={settings.contact_phone || ''} 
                    onChange={(e) => updateSetting('contact_phone', e.target.value)}
                    placeholder="+62 812 6201 7001"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Email</label>
                  <Input 
                    value={settings.contact_email || ''} 
                    onChange={(e) => updateSetting('contact_email', e.target.value)}
                    placeholder="mytentor2017@gmail.com"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-slate-700">Alamat</label>
                  <Input 
                    value={settings.contact_address || ''} 
                    onChange={(e) => updateSetting('contact_address', e.target.value)}
                    placeholder="Jl. Ayahanda No. 18 Medan Petisah"
                  />
                </div>
              </div>
            </div>
            <div className="border-t pt-4 mt-4">
              <h4 className="font-medium mb-3">WhatsApp & Social</h4>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700">Nomor WhatsApp (tanpa +)</label>
                  <Input 
                    value={settings.whatsapp_number || ''} 
                    onChange={(e) => updateSetting('whatsapp_number', e.target.value)}
                    placeholder="6281262017001"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Pesan WhatsApp Default</label>
                  <Input 
                    value={settings.whatsapp_message || ''} 
                    onChange={(e) => updateSetting('whatsapp_message', e.target.value)}
                    placeholder="Halo Admin MyTentor..."
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">YouTube Channel</label>
                  <Input 
                    value={settings.youtube_channel || ''} 
                    onChange={(e) => updateSetting('youtube_channel', e.target.value)}
                    placeholder="https://www.youtube.com/@mytentorofficial"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Footer Description</label>
                  <Input 
                    value={settings.footer_description || ''} 
                    onChange={(e) => updateSetting('footer_description', e.target.value)}
                    placeholder="Platform CBT/CAT terpercaya..."
                  />
                </div>
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* Bank/Rekening Section */}
        <TabsContent value="bank" className="space-y-4">
          <Card className="p-6">
            <h3 className="font-semibold text-lg mb-4">Info Rekening Bank (untuk Transfer Manual)</h3>
            <p className="text-sm text-slate-500 mb-6">
              Informasi ini akan ditampilkan di halaman pembayaran sebagai alternatif jika pembayaran Midtrans bermasalah.
            </p>
            <div className="grid gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Nama Bank</label>
                <Input 
                  value={settings.bank_name || ''} 
                  onChange={(e) => setSettings({...settings, bank_name: e.target.value})}
                  placeholder="BCA, Mandiri, BRI, BNI, dll"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Nomor Rekening</label>
                <Input 
                  value={settings.bank_account_number || ''} 
                  onChange={(e) => setSettings({...settings, bank_account_number: e.target.value})}
                  placeholder="1234567890"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Atas Nama</label>
                <Input 
                  value={settings.bank_account_name || ''} 
                  onChange={(e) => setSettings({...settings, bank_account_name: e.target.value})}
                  placeholder="PT MyTentor Indonesia"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Catatan untuk User</label>
                <Input 
                  value={settings.bank_info_note || ''} 
                  onChange={(e) => setSettings({...settings, bank_info_note: e.target.value})}
                  placeholder="Konfirmasi pembayaran via WhatsApp setelah transfer"
                />
              </div>
            </div>
            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              <p className="font-medium">Tips:</p>
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li>Kosongkan semua field jika tidak ingin menampilkan opsi transfer manual</li>
                <li>Tambahkan catatan agar user tahu cara konfirmasi pembayaran</li>
                <li>Info ini akan muncul di popup pembayaran pada dashboard siswa</li>
              </ul>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Image Gallery Dialog */}
      <Dialog open={imageGalleryOpen} onOpenChange={setImageGalleryOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Pilih Gambar</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Upload Section */}
            <div className="p-4 border-2 border-dashed border-slate-200 rounded-lg text-center">
              <input 
                type="file" 
                ref={fileInputRef}
                onChange={handleImageUpload}
                accept="image/*"
                className="hidden"
              />
              <Button 
                variant="outline" 
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingImage}
              >
                {uploadingImage ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4 mr-2" />
                )}
                Upload Gambar Baru
              </Button>
              <p className="text-xs text-slate-500 mt-2">Max 5MB, format: JPG, PNG, WebP</p>
            </div>

            {/* Gallery */}
            <div>
              <h4 className="font-medium mb-3">Galeri Gambar</h4>
              <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                {imageGallery.map((img, index) => (
                  <div 
                    key={index}
                    className="relative cursor-pointer group"
                    onClick={() => selectFromGallery(img.url)}
                  >
                    <img 
                      src={img.url} 
                      alt={img.label} 
                      className="w-full h-24 object-cover rounded-lg border-2 border-transparent group-hover:border-[#4472C4] transition-all"
                    />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-white" />
                    </div>
                    <p className="text-xs text-slate-500 mt-1 truncate">{img.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Preview Landing Page</DialogTitle>
          </DialogHeader>
          <div className="border rounded-lg overflow-hidden">
            <div className="bg-[#4472C4] text-white p-6">
              <Badge className="bg-white/20 text-white mb-3">{settings.hero_badge_text}</Badge>
              <p className="text-sm opacity-90">{settings.hero_subtitle}</p>
              <h1 className="text-2xl font-bold">{settings.hero_title}</h1>
              <p className="text-sm opacity-80 mt-2">{settings.hero_description}</p>
              {settings.hero_image && (
                <img src={settings.hero_image} alt="Hero" className="w-full h-40 object-cover rounded-lg mt-4" />
              )}
            </div>
            <div className="p-4 text-center text-sm text-slate-500">
              Preview singkat. Buka landing page untuk melihat tampilan lengkap.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewOpen(false)}>Tutup</Button>
            <Button onClick={() => window.open('/', '_blank')}>
              <ExternalLink className="w-4 h-4 mr-2" />
              Buka Landing Page
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
